import { LRUCache } from '../lib/lru-cache';
import { RenderContext } from '../render/context/createRenderContext';
import { Provider } from './provider';

// Re-export types from text.ts for convenience
type Grapheme = {
  width: number;
  height: number;
  x: number;
  y: number;
  char: string;
  font: string;
  charWidth: number;
  letterSpacing: number;
  alignSpacing: number;
};

export interface TextLayoutCacheItem {
  /** Cache key for invalidation check */
  cacheKey: string;
  /** Whether font is loaded */
  fontLoaded: boolean;
  /** Text layout result from wrapText */
  textLayout: {
    fontSettings: any;
    fontValue: string;
    safeBoxSize: { w: number; h: number };
    safeWidth: number;
    text: string;
    lines: string[][];
    linesBreakType: number[];
    width: number;
    height: number;
  } | null;
  /** Grapheme layout for each line */
  graphemeLayout: Grapheme[][] | null;
  /** Last access timestamp for LRU eviction */
  lastAccess: number;
}

/**
 * Cache statistics for monitoring and debugging
 */
export interface TextLayoutCacheStats {
  nodeLayoutHits: number;
  nodeLayoutMisses: number;
  wrapTextHits: number;
  wrapTextMisses: number;
  totalNodes: number;
  wrapTextCacheSize: number;
}

/**
 * Wrap text cache key generator
 * Creates a unique key based on all properties that affect text wrapping
 */
function createWrapTextCacheKey(
  text: string,
  fontValue: string,
  width: number,
  letterSpacing: number,
  textAlign: string,
  wrapMode: string
): string {
  // Round width to reduce cache fragmentation (1px precision is sufficient)
  const roundedWidth = Math.round(width);
  return `${fontValue}|${roundedWidth}|${letterSpacing}|${textAlign}|${wrapMode}|${text}`;
}

/**
 * Provider for managing text node layout cache.
 * This replaces the old pattern of storing __textLayout and __graphemeLayout on nodes directly.
 *
 * Enhanced with:
 * - LRU eviction for node layout cache
 * - Shared wrap text results cache (same text + style = same layout)
 * - Cache statistics for monitoring
 */
export class TextLayoutProvider extends Provider {
  /**
   * LRU cache from node.id to text layout cache item
   * Using LRU to automatically evict stale entries
   */
  private _nodeLayoutCache: LRUCache<string, TextLayoutCacheItem>;

  /**
   * Shared wrap text results cache
   * Key: wrapTextCacheKey (font + width + spacing + text)
   * Value: wrap text result (lines, linesBreakType, width, height)
   *
   * This allows sharing layout results across nodes with identical text styling
   */
  private _wrapTextCache: LRUCache<
    string,
    {
      lines: string[][];
      linesBreakType: number[];
      width: number;
      height: number;
    }
  >;

  /**
   * Cache statistics
   */
  private _stats: TextLayoutCacheStats;

  /**
   * Maximum number of nodes to cache
   */
  private _maxNodeCacheSize: number;

  /**
   * Maximum number of wrap text results to cache
   */
  private _maxWrapTextCacheSize: number;

  constructor(options?: { maxNodeCacheSize?: number; maxWrapTextCacheSize?: number }) {
    super();
    this._maxNodeCacheSize = options?.maxNodeCacheSize ?? 2000;
    this._maxWrapTextCacheSize = options?.maxWrapTextCacheSize ?? 2000;

    this._nodeLayoutCache = new LRUCache<string, TextLayoutCacheItem>(this._maxNodeCacheSize);
    this._wrapTextCache = new LRUCache(this._maxWrapTextCacheSize);

    this._stats = {
      nodeLayoutHits: 0,
      nodeLayoutMisses: 0,
      wrapTextHits: 0,
      wrapTextMisses: 0,
      totalNodes: 0,
      wrapTextCacheSize: 0,
    };
  }

  flush() {
    if (this.disposed) return;
  }

  attach(renderContext: RenderContext) {
    super.attach(renderContext);
  }

  dispose() {
    this._nodeLayoutCache.clear();
    this._wrapTextCache.clear();
    this._resetStats();
    super.dispose();
  }

  clear() {
    this._nodeLayoutCache.clear();
    this._wrapTextCache.clear();
    this._resetStats();
  }

  private _resetStats() {
    this._stats = {
      nodeLayoutHits: 0,
      nodeLayoutMisses: 0,
      wrapTextHits: 0,
      wrapTextMisses: 0,
      totalNodes: 0,
      wrapTextCacheSize: 0,
    };
  }

  // ============================================
  // Statistics and monitoring
  // ============================================

  /**
   * Get cache statistics
   */
  getStats(): TextLayoutCacheStats {
    return {
      ...this._stats,
      totalNodes: this._nodeLayoutCache.size,
      wrapTextCacheSize: this._wrapTextCache.size,
    };
  }

  /**
   * Get hit rate for node layout cache
   */
  getNodeLayoutHitRate(): number {
    const total = this._stats.nodeLayoutHits + this._stats.nodeLayoutMisses;
    return total > 0 ? this._stats.nodeLayoutHits / total : 0;
  }

  /**
   * Get hit rate for wrap text cache
   */
  getWrapTextHitRate(): number {
    const total = this._stats.wrapTextHits + this._stats.wrapTextMisses;
    return total > 0 ? this._stats.wrapTextHits / total : 0;
  }

  // ============================================
  // Cache CRUD operations
  // ============================================

  /**
   * Get layout cache for a node
   * @param nodeId The node id
   * @returns The layout cache item or undefined
   */
  getLayoutCache(nodeId: string): TextLayoutCacheItem | undefined {
    const cache = this._nodeLayoutCache.get(nodeId);
    if (cache) {
      this._stats.nodeLayoutHits++;
      cache.lastAccess = Date.now();
    } else {
      this._stats.nodeLayoutMisses++;
    }
    return cache;
  }

  /**
   * Set layout cache for a node
   * @param nodeId The node id
   * @param cache The layout cache item
   */
  setLayoutCache(nodeId: string, cache: TextLayoutCacheItem): void {
    cache.lastAccess = Date.now();
    this._nodeLayoutCache.set(nodeId, cache);
  }

  /**
   * Remove layout cache for a node
   * @param nodeId The node id
   */
  removeLayoutCache(nodeId: string): void {
    this._nodeLayoutCache.delete(nodeId);
  }

  /**
   * Check if a node has layout cache
   * @param nodeId The node id
   * @returns True if the node has layout cache
   */
  hasLayoutCache(nodeId: string): boolean {
    return this._nodeLayoutCache.has(nodeId);
  }

  // ============================================
  // Shared wrap text cache operations
  // ============================================

  /**
   * Get cached wrap text result
   * @param text The text content
   * @param fontValue The font value string
   * @param width The container width
   * @param letterSpacing Letter spacing
   * @param textAlign Text alignment
   * @param wrapMode Wrap mode
   * @returns Cached wrap text result or undefined
   */
  getWrapTextCache(
    text: string,
    fontValue: string,
    width: number,
    letterSpacing: number,
    textAlign: string,
    wrapMode: string
  ): { lines: string[][]; linesBreakType: number[]; width: number; height: number } | undefined {
    const key = createWrapTextCacheKey(text, fontValue, width, letterSpacing, textAlign, wrapMode);
    const cached = this._wrapTextCache.get(key);
    if (cached) {
      this._stats.wrapTextHits++;
    } else {
      this._stats.wrapTextMisses++;
    }
    return cached;
  }

  /**
   * Set wrap text cache
   * @param text The text content
   * @param fontValue The font value string
   * @param width The container width
   * @param letterSpacing Letter spacing
   * @param textAlign Text alignment
   * @param wrapMode Wrap mode
   * @param result The wrap text result
   */
  setWrapTextCache(
    text: string,
    fontValue: string,
    width: number,
    letterSpacing: number,
    textAlign: string,
    wrapMode: string,
    result: { lines: string[][]; linesBreakType: number[]; width: number; height: number }
  ): void {
    const key = createWrapTextCacheKey(text, fontValue, width, letterSpacing, textAlign, wrapMode);
    this._wrapTextCache.set(key, result);
  }

  /**
   * Check if wrap text result is cached
   */
  hasWrapTextCache(
    text: string,
    fontValue: string,
    width: number,
    letterSpacing: number,
    textAlign: string,
    wrapMode: string
  ): boolean {
    const key = createWrapTextCacheKey(text, fontValue, width, letterSpacing, textAlign, wrapMode);
    return this._wrapTextCache.has(key);
  }

  // ============================================
  // Text Layout accessors
  // ============================================

  /**
   * Get text layout for a node
   * @param nodeId The node id
   * @returns The text layout or null
   */
  getTextLayout(nodeId: string): TextLayoutCacheItem['textLayout'] {
    return this._nodeLayoutCache.get(nodeId)?.textLayout ?? null;
  }

  /**
   * Set text layout for a node
   * @param nodeId The node id
   * @param textLayout The text layout
   * @param fontLoaded Whether font is loaded
   * @param cacheKey Optional cache key for invalidation
   */
  setTextLayout(
    nodeId: string,
    textLayout: TextLayoutCacheItem['textLayout'],
    fontLoaded: boolean,
    cacheKey?: string
  ): void {
    const existing = this._nodeLayoutCache.get(nodeId);
    const now = Date.now();
    if (existing) {
      existing.textLayout = textLayout;
      existing.fontLoaded = fontLoaded;
      existing.lastAccess = now;
      if (cacheKey !== undefined) {
        existing.cacheKey = cacheKey;
      }
    } else {
      this._nodeLayoutCache.set(nodeId, {
        cacheKey: cacheKey ?? '',
        fontLoaded,
        textLayout,
        graphemeLayout: null,
        lastAccess: now,
      });
    }
  }

  /**
   * Check if text layout exists for a node
   * @param nodeId The node id
   * @returns True if text layout exists
   */
  hasTextLayout(nodeId: string): boolean {
    const cache = this._nodeLayoutCache.get(nodeId);
    return cache?.textLayout != null;
  }

  // ============================================
  // Grapheme Layout accessors
  // ============================================

  /**
   * Get grapheme layout for a node
   * @param nodeId The node id
   * @returns The grapheme layout or null
   */
  getGraphemeLayout(nodeId: string): Grapheme[][] | null {
    return this._nodeLayoutCache.get(nodeId)?.graphemeLayout ?? null;
  }

  /**
   * Set grapheme layout for a node
   * @param nodeId The node id
   * @param graphemeLayout The grapheme layout
   */
  setGraphemeLayout(nodeId: string, graphemeLayout: Grapheme[][] | null): void {
    const existing = this._nodeLayoutCache.get(nodeId);
    const now = Date.now();
    if (existing) {
      existing.graphemeLayout = graphemeLayout;
      existing.lastAccess = now;
    } else {
      this._nodeLayoutCache.set(nodeId, {
        cacheKey: '',
        fontLoaded: false,
        textLayout: null,
        graphemeLayout,
        lastAccess: now,
      });
    }
  }

  /**
   * Check if grapheme layout exists for a node
   * @param nodeId The node id
   * @returns True if grapheme layout exists
   */
  hasGraphemeLayout(nodeId: string): boolean {
    const cache = this._nodeLayoutCache.get(nodeId);
    return cache?.graphemeLayout != null;
  }

  // ============================================
  // Cache validation helpers
  // ============================================

  /**
   * Get the cache key for a node
   * @param nodeId The node id
   * @returns The cache key or empty string
   */
  getCacheKey(nodeId: string): string {
    return this._nodeLayoutCache.get(nodeId)?.cacheKey ?? '';
  }

  /**
   * Check if cache is valid by comparing cache keys
   * @param nodeId The node id
   * @param newCacheKey The new cache key to compare
   * @returns True if cache is valid (keys match)
   */
  isCacheValid(nodeId: string, newCacheKey: string): boolean {
    const existing = this._nodeLayoutCache.get(nodeId);
    return existing?.cacheKey === newCacheKey;
  }

  /**
   * Get font loaded status for a node
   * @param nodeId The node id
   * @returns True if font is loaded
   */
  isFontLoaded(nodeId: string): boolean {
    return this._nodeLayoutCache.get(nodeId)?.fontLoaded ?? false;
  }
}

// ============================================
// Helper functions for working with TextLayoutProvider
// ============================================

/**
 * Create a cache key for text layout invalidation
 * @param node The text node
 * @returns Cache key string
 */
export function createTextLayoutCacheKey(node: any): string {
  // Include all properties that affect text layout
  // Use a more efficient string building approach
  const parts = [
    node.text ?? '',
    node.fontFamily ?? '',
    node.fontSize ?? 12,
    node.fontWeight ?? 'normal',
    node.fontStyle ?? 'normal',
    node.letterSpacing ?? 0,
    node.lineHeight ?? 1.2,
    node.textAlign ?? 'left',
    Math.round(node.w ?? 0), // Round width to reduce cache misses
    node.padding ?? '',
    node.width ?? '', // layout sizing mode
  ];
  return parts.join('|');
}

/**
 * Create a cache key for wrap text results (shared across nodes)
 * This key is used for the shared wrap text cache
 */
export function createWrapTextSharedCacheKey(
  text: string,
  fontValue: string,
  width: number,
  letterSpacing: number,
  textAlign: string,
  wrapMode: string
): string {
  return `${fontValue}|${Math.round(width)}|${letterSpacing}|${textAlign}|${wrapMode}|${text}`;
}

/**
 * Get text layout cache from provider
 * @param renderContext The render context
 * @param nodeId The node id
 * @returns The text layout cache item or undefined
 */
export function getNodeTextLayoutCache(
  renderContext: RenderContext,
  nodeId: string
): TextLayoutCacheItem | undefined {
  const provider = renderContext.providers.textLayout as TextLayoutProvider;
  return provider?.getLayoutCache(nodeId);
}

/**
 * Get text layout for a node from provider
 * @param renderContext The render context
 * @param nodeId The node id
 * @returns The text layout or null
 */
export function getNodeTextLayout(
  renderContext: RenderContext,
  nodeId: string
): TextLayoutCacheItem['textLayout'] {
  const provider = renderContext.providers.textLayout as TextLayoutProvider;
  return provider?.getTextLayout(nodeId) ?? null;
}

/**
 * Get grapheme layout for a node from provider
 * @param renderContext The render context
 * @param nodeId The node id
 * @returns The grapheme layout or null
 */
export function getNodeGraphemeLayout(
  renderContext: RenderContext,
  nodeId: string
): Grapheme[][] | null {
  const provider = renderContext.providers.textLayout as TextLayoutProvider;
  return provider?.getGraphemeLayout(nodeId) ?? null;
}

/**
 * Get text layout provider from render context
 * @param renderContext The render context
 * @returns The text layout provider or null
 */
export function getTextLayoutProvider(renderContext: RenderContext): TextLayoutProvider | null {
  return (renderContext.providers?.textLayout as TextLayoutProvider) ?? null;
}

/**
 * Get text layout cache statistics
 * @param renderContext The render context
 * @returns Cache statistics or null
 */
export function getTextLayoutCacheStats(renderContext: RenderContext): TextLayoutCacheStats | null {
  const provider = renderContext.providers?.textLayout as TextLayoutProvider;
  return provider?.getStats() ?? null;
}

/**
 * Try to get cached wrap text result from shared cache
 * @param renderContext The render context
 * @param text The text content
 * @param fontValue The font value
 * @param width Container width
 * @param letterSpacing Letter spacing
 * @param textAlign Text alignment
 * @param wrapMode Wrap mode
 * @returns Cached result or undefined
 */
export function getCachedWrapTextResult(
  renderContext: RenderContext,
  text: string,
  fontValue: string,
  width: number,
  letterSpacing: number,
  textAlign: string,
  wrapMode: string
): { lines: string[][]; linesBreakType: number[]; width: number; height: number } | undefined {
  const provider = renderContext.providers?.textLayout as TextLayoutProvider;
  return provider?.getWrapTextCache(text, fontValue, width, letterSpacing, textAlign, wrapMode);
}

/**
 * Cache wrap text result to shared cache
 * @param renderContext The render context
 * @param text The text content
 * @param fontValue The font value
 * @param width Container width
 * @param letterSpacing Letter spacing
 * @param textAlign Text alignment
 * @param wrapMode Wrap mode
 * @param result The wrap text result
 */
export function setCachedWrapTextResult(
  renderContext: RenderContext,
  text: string,
  fontValue: string,
  width: number,
  letterSpacing: number,
  textAlign: string,
  wrapMode: string,
  result: { lines: string[][]; linesBreakType: number[]; width: number; height: number }
): void {
  const provider = renderContext.providers?.textLayout as TextLayoutProvider;
  provider?.setWrapTextCache(text, fontValue, width, letterSpacing, textAlign, wrapMode, result);
}
